import keyBy from 'lodash-es/keyBy'

import {
  EMPTY_OBJECT,
  FLOATING_ELEMENT_PLACEMENT,
} from '../../../constants/common'
import {
  LOCAL_STORAGE_BIRDVIEW_CHARTS_COLUMN_SPANS_KEY,
  LOCAL_STORAGE_BIRDVIEW_CHARTS_ORDER_KEY,
  LOCAL_STORAGE_BIRDVIEW_CHARTS_ROW_SPANS_KEY,
} from '../../../constants/storage'
import {
  detectDragDropPointerCollision,
  DragDropProvider,
  DragDropSortableList,
} from '../../DragDrop'
import {
  BirdviewChartCatalog,
  BirdviewChartCatalogAddCard,
} from '../BirdviewChartCatalog'
import { useBirdviewTileRowOrder } from '../BirdviewTileRow'
import {
  getBirdviewStorageItemSetKey,
  getBirdviewStorageKey,
} from '../DashboardBirdview.utils.storage'

import { BIRDVIEW_CHART_GRID_SORTABLE_ID } from './BirdviewChartGrid.constants'
import { useBirdviewChartGridSpans } from './BirdviewChartGrid.hooks'
import {
  checkIsBirdviewChartGridAddCardShown,
  checkIsBirdviewChartGridItemShown,
  getBirdviewChartGridDefaultColumnSpans,
  getBirdviewChartGridDefaultRowSpans,
  getBirdviewChartGridItemId,
  getBirdviewChartGridNextColumnSpan,
  sortBirdviewChartGridItems,
} from './BirdviewChartGrid.utils'
import { BirdviewChartGridEmpty } from './BirdviewChartGridEmpty'
import { BirdviewChartGridItem } from './BirdviewChartGridItem'

import * as styles from './BirdviewChartGrid.module.css'

function BirdviewChartGrid({
  catalogProps,
  hiddenIds,
  items,
  renderItem,
  storageScope,
}) {
  const itemById = keyBy(items, getBirdviewChartGridItemId)
  const defaultOrder = items.map(getBirdviewChartGridItemId)
  const itemSetKey = getBirdviewStorageItemSetKey(defaultOrder)

  const { handleDragEnd, order } = useBirdviewTileRowOrder({
    defaultOrder,
    storageKeyPrefix: getBirdviewStorageKey(
      LOCAL_STORAGE_BIRDVIEW_CHARTS_ORDER_KEY,
      [storageScope]
    ),
  })
  const { setSpan: setColumnSpan, spans: columnSpans } =
    useBirdviewChartGridSpans({
      defaultSpans: getBirdviewChartGridDefaultColumnSpans(items),
      storageKey: getBirdviewStorageKey(
        LOCAL_STORAGE_BIRDVIEW_CHARTS_COLUMN_SPANS_KEY,
        [storageScope, itemSetKey]
      ),
    })
  const { setSpan: setRowSpan, spans: rowSpans } = useBirdviewChartGridSpans({
    defaultSpans: getBirdviewChartGridDefaultRowSpans(items),
    storageKey: getBirdviewStorageKey(
      LOCAL_STORAGE_BIRDVIEW_CHARTS_ROW_SPANS_KEY,
      [storageScope, itemSetKey]
    ),
  })

  // A hidden card keeps its place in the stored order; the sortable list only
  // gets the rendered ids.
  const shownItems = order
    .map(id => itemById[id])
    .filter(item => checkIsBirdviewChartGridItemShown({ hiddenIds, item }))
  const shownIds = shownItems.map(getBirdviewChartGridItemId)

  function handleCycleSize(id) {
    setColumnSpan(id, getBirdviewChartGridNextColumnSpan(columnSpans[id]))
  }

  function renderCardItem(item, dragHandleProps) {
    return renderItem(item, dragHandleProps, { onCycleSize: handleCycleSize })
  }

  function renderOverlay(activeId) {
    const item = itemById[activeId]

    return item ? (
      <div className={styles.overlay}>{renderCardItem(item, EMPTY_OBJECT)}</div>
    ) : null
  }

  function renderAddCard() {
    return (
      <BirdviewChartCatalog
        {...catalogProps}
        Trigger={BirdviewChartCatalogAddCard}
        placement={FLOATING_ELEMENT_PLACEMENT.bottom}
      />
    )
  }

  function getResizeHandler(setSpan, id) {
    return function handleResize(span) {
      setSpan(id, span)
    }
  }

  return shownItems.length > 0 ? (
    <DragDropProvider
      collisionDetection={detectDragDropPointerCollision}
      renderOverlay={renderOverlay}
      onDragEnd={handleDragEnd}
    >
      <DragDropSortableList
        className={styles.grid}
        id={BIRDVIEW_CHART_GRID_SORTABLE_ID}
        items={shownIds}
        strategy={sortBirdviewChartGridItems}
      >
        {shownItems.map(item => {
          const id = getBirdviewChartGridItemId(item)

          return (
            <BirdviewChartGridItem
              columnSpan={columnSpans[id]}
              id={id}
              key={id}
              rowSpan={rowSpans[id]}
              onResizeColumn={getResizeHandler(setColumnSpan, id)}
              onResizeRow={getResizeHandler(setRowSpan, id)}
            >
              {dragHandleProps => renderCardItem(item, dragHandleProps)}
            </BirdviewChartGridItem>
          )
        })}
        {checkIsBirdviewChartGridAddCardShown(catalogProps) ? (
          <div className={styles.addCell}>{renderAddCard()}</div>
        ) : null}
      </DragDropSortableList>
    </DragDropProvider>
  ) : (
    <BirdviewChartGridEmpty>
      {checkIsBirdviewChartGridAddCardShown(catalogProps)
        ? renderAddCard()
        : null}
    </BirdviewChartGridEmpty>
  )
}

export default BirdviewChartGrid
